
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Bold,
  Code2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Palette,
  Quote,
  Redo2,
  RemoveFormatting,
  Smile,
  Strikethrough,
  Underline,
  Undo2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { InsertImageDialog } from '@/components/painel/cms/InsertImageDialog';
import { InsertLinkDialog } from '@/components/painel/cms/InsertLinkDialog';
import { InsertSmilieDialog } from '@/components/painel/cms/InsertSmilieDialog';
import { InsertPowerBbcodeDialog, type PowerBbcodeMode } from '@/components/painel/cms/InsertPowerBbcodeDialog';
import { InsertAceMentionDialog } from '@/components/painel/cms/InsertAceMentionDialog';
import {
  AceNavIcon,
  PawnNavIcon,
  PowerNavIcon,
  SmileNavIcon,
} from '@/components/help-center/MaskedSvgIcon';
import type { CmsRichImageVariant } from '@/lib/cms-rich-helpers';
import { normalizeEditorHtml } from '@/lib/cms-html';
import { useRichEditorDoubleParenSmilie } from '@/hooks/useRichEditorDoubleParenSmilie';
import { useRichEditorSelection } from '@/hooks/use-rich-editor-selection';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export type RichTextEditorToolbar = 'standard' | 'extended';
export type RichTextEditorTone = 'painel' | 'info' | 'tip' | 'warning';

type RichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  className?: string;
  disabled?: boolean;
  /** Repõe o DOM quando muda (ex.: abrir outra seção). */
  resetKey?: string;
  /** Link, imagem, citação, trecho código (consola), limpar formatação. */
  toolbar?: RichTextEditorToolbar;
  editorMinHeightClass?: string;
  /** Aparência do contentor: cores do painel ou das caixas do artigo. */
  tone?: RichTextEditorTone;
  /** Toolbar aparece flutuando acima do campo ao focar (ex.: editor inline da Lore). */
  floatingToolbar?: boolean;
};

function exec(cmd: string, valueArg?: string) {
  try {
    document.execCommand(cmd, false, valueArg);
  } catch {
    /* ignore */
  }
}

function ToolbarGroup({ children }: { children: ReactNode }) {
  return <div className="flex items-center gap-0.5">{children}</div>;
}

function ToolbarSep({ tone }: { tone: RichTextEditorTone }) {
  const isPainel = tone === 'painel';
  const gradient = isPainel
    ? 'linear-gradient(to bottom, transparent 0%, rgba(148, 163, 184, 0.12) 15%, rgba(148, 163, 184, 0.45) 50%, rgba(148, 163, 184, 0.12) 85%, transparent 100%)'
    : tone === 'info'
      ? 'linear-gradient(to bottom, transparent 0%, rgba(37, 99, 235, 0.18) 15%, rgba(37, 99, 235, 0.42) 50%, rgba(37, 99, 235, 0.18) 85%, transparent 100%)'
      : tone === 'tip'
        ? 'linear-gradient(to bottom, transparent 0%, rgba(22, 163, 74, 0.18) 15%, rgba(22, 163, 74, 0.42) 50%, rgba(22, 163, 74, 0.18) 85%, transparent 100%)'
        : 'linear-gradient(to bottom, transparent 0%, rgba(234, 88, 12, 0.2) 15%, rgba(234, 88, 12, 0.45) 50%, rgba(234, 88, 12, 0.2) 85%, transparent 100%)';

  return (
    <span
      role="separator"
      aria-hidden
      className="mx-2.5 flex h-[18px] shrink-0 items-center self-center"
    >
      <span
        style={{
          display: 'block',
          width: 1,
          height: '100%',
          borderRadius: 999,
          background: gradient,
          boxShadow: isPainel
            ? '1px 0 0 rgba(15, 23, 42, 0.55)'
            : '1px 0 0 rgba(255, 255, 255, 0.55)',
        }}
      />
    </span>
  );
}

const TONE_STYLES: Record<
  RichTextEditorTone,
  {
    root: string;
    tb: string;
    tbBtn: string;
    editor: string;
  }
> = {
  painel: {
    root: 'overflow-hidden rounded-xl border border-white/10 bg-white/[0.04] text-left shadow-inner',
    tb: 'flex flex-wrap items-center gap-y-1.5 rounded-t-xl border border-white/10 border-b-0 bg-white/[0.05] px-2.5 py-2',
    tbBtn: 'h-8 w-8 shrink-0 rounded-lg p-0 text-blue-100/90 hover:bg-white/10 hover:text-sky-100',
    editor: cn(
      'max-h-[520px] overflow-y-auto px-3 py-2 text-left font-inter text-sm leading-relaxed text-blue-50',
      '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
      '[&_a]:text-sky-300 [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-sky-500/50 [&_blockquote]:pl-3',
      '[&_.cms-rt-resize]:max-w-full [&_.cms-rt-resize_img]:max-w-full [&_img[data-xat-smilie]]:inline-block',
      'focus:outline-none focus:ring-2 focus:ring-sky-400/25'
    ),
  },
  info: {
    root: 'overflow-hidden rounded-xl border border-blue-200 bg-blue-50 text-left shadow-sm',
    tb: 'flex flex-wrap items-center gap-y-1.5 rounded-t-xl border border-blue-200 border-b-0 bg-blue-100/80 px-2.5 py-2',
    tbBtn: 'h-8 w-8 shrink-0 rounded-lg p-0 text-blue-900 hover:bg-blue-200/60',
    editor: cn(
      'rounded-b-xl border border-blue-200 border-t-0 bg-white max-h-[520px] overflow-y-auto px-3 py-2 text-left font-inter text-sm leading-relaxed text-blue-950',
      '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
      '[&_a]:text-blue-700 [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-blue-400 [&_blockquote]:pl-3',
      '[&_.cms-rt-resize]:max-w-full [&_.cms-rt-resize_img]:max-w-full [&_img[data-xat-smilie]]:inline-block',
      'focus:outline-none focus:ring-2 focus:ring-blue-300/50'
    ),
  },
  tip: {
    root: 'overflow-hidden rounded-xl border border-green-200 bg-green-50 text-left shadow-sm',
    tb: 'flex flex-wrap items-center gap-y-1.5 rounded-t-xl border border-green-200 border-b-0 bg-green-100/80 px-2.5 py-2',
    tbBtn: 'h-8 w-8 shrink-0 rounded-lg p-0 text-green-900 hover:bg-green-200/60',
    editor: cn(
      'rounded-b-xl border border-green-200 border-t-0 bg-white max-h-[520px] overflow-y-auto px-3 py-2 text-left font-inter text-sm leading-relaxed text-green-950',
      '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
      '[&_a]:text-green-800 [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-green-500 [&_blockquote]:pl-3',
      '[&_.cms-rt-resize]:max-w-full [&_.cms-rt-resize_img]:max-w-full [&_img[data-xat-smilie]]:inline-block',
      'focus:outline-none focus:ring-2 focus:ring-green-300/50'
    ),
  },
  warning: {
    root: 'overflow-hidden rounded-xl border border-orange-200 bg-orange-50 text-left shadow-sm',
    tb: 'flex flex-wrap items-center gap-y-1.5 rounded-t-xl border border-orange-200 border-b-0 bg-orange-100/80 px-2.5 py-2',
    tbBtn: 'h-8 w-8 shrink-0 rounded-lg p-0 text-orange-900 hover:bg-orange-200/60',
    editor: cn(
      'rounded-b-xl border border-orange-200 border-t-0 bg-white max-h-[520px] overflow-y-auto px-3 py-2 text-left font-inter text-sm leading-relaxed text-orange-950',
      '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
      '[&_a]:text-orange-800 [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-orange-500 [&_blockquote]:pl-3',
      '[&_.cms-rt-resize]:max-w-full [&_.cms-rt-resize_img]:max-w-full [&_img[data-xat-smilie]]:inline-block',
      'focus:outline-none focus:ring-2 focus:ring-orange-300/50'
    ),
  },
};

export function RichTextEditor({
  value,
  onChange,
  className,
  disabled,
  resetKey,
  toolbar = 'standard',
  editorMinHeightClass = 'min-h-[220px]',
  tone = 'painel',
  floatingToolbar = false,
}: RichTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  /** Último HTML que emitimos via onChange — evita reaplicar o value e resetar o cursor. */
  const lastEmittedRef = useRef<string | null>(null);
  const { saveSelection, focusAndRestoreSelection, insertHtmlAtSelection } =
    useRichEditorSelection(ref);
  const [imageDlg, setImageDlg] = useState(false);
  const [linkDlg, setLinkDlg] = useState(false);
  const [linkSelectedText, setLinkSelectedText] = useState('');
  const [colorOpen, setColorOpen] = useState(false);
  const [smilieDlg, setSmilieDlg] = useState(false);
  const [powerBbcodeMode, setPowerBbcodeMode] = useState<PowerBbcodeMode | null>(null);
  const [aceDlg, setAceDlg] = useState(false);
  const [toolbarVisible, setToolbarVisible] = useState(!floatingToolbar);
  const blurHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dialogOpenRef = useRef(false);

  const writeDom = useCallback((html: string) => {
    const el = ref.current;
    if (!el) return;
    el.innerHTML = html || '';
    lastEmittedRef.current = normalizeEditorHtml(html || '');
  }, []);

  // Carga inicial / troca de secção (resetKey) — força o DOM mesmo com foco.
  useEffect(() => {
    writeDom(value || '');
    // value intencional: só queremos forçar quando resetKey muda (ou no mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetKey drives remount of content
  }, [resetKey, writeDom]);

  // Actualizações externas (carregar outro power, undo do pai, etc.).
  // Nunca reescrever o DOM enquanto o utilizador escreve — isso manda o cursor para o início.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = normalizeEditorHtml(value || '');
    if (next === lastEmittedRef.current) return;
    if (document.activeElement === el) return;
    writeDom(value || '');
  }, [value, writeDom]);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const html = normalizeEditorHtml(el.innerHTML);
    lastEmittedRef.current = html;
    onChange(html);
  }, [onChange]);

  useRichEditorDoubleParenSmilie(ref, sync, disabled);

  const imageVariant: CmsRichImageVariant = tone === 'painel' ? 'painel' : 'article';

  const runCommand = useCallback(
    (fn: () => void) => {
      focusAndRestoreSelection();
      fn();
      saveSelection();
      sync();
    },
    [focusAndRestoreSelection, saveSelection, sync]
  );

  const insertHtml = useCallback(
    (html: string) => {
      insertHtmlAtSelection(html);
      sync();
    },
    [insertHtmlAtSelection, sync]
  );

  const onInput = () => {
    saveSelection();
    sync();
  };

  const sty = TONE_STYLES[tone];

  const anyDialogOpen =
    imageDlg || linkDlg || smilieDlg || powerBbcodeMode != null || aceDlg || colorOpen;
  dialogOpenRef.current = anyDialogOpen;

  useEffect(() => {
    return () => {
      if (blurHideTimer.current) clearTimeout(blurHideTimer.current);
    };
  }, []);

  const showFloatingChrome = floatingToolbar && (toolbarVisible || anyDialogOpen);

  const tb = cn(
    sty.tb,
    disabled && 'pointer-events-none opacity-50',
    floatingToolbar &&
      'absolute bottom-[calc(100%+0.5rem)] left-0 z-30 w-max max-w-[min(100%,36rem)] rounded-xl border border-white/15 bg-[#0a0830]/95 shadow-xl shadow-black/40 backdrop-blur-md',
    floatingToolbar && !showFloatingChrome && 'pointer-events-none invisible opacity-0'
  );

  const tbBtn = sty.tbBtn;

  const insertLink = () => {
    focusAndRestoreSelection();
    saveSelection();
    const sel = window.getSelection()?.toString() ?? '';
    setLinkSelectedText(sel);
    setLinkDlg(true);
  };

  const applyLink = (payload: { url: string; text: string }) => {
    focusAndRestoreSelection();
    const safeUrl = payload.url.replace(/"/g, '&quot;');
    const safeText = payload.text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    insertHtmlAtSelection(`<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${safeText}</a>`);
    sync();
  };

  const TEXT_COLORS = [
    { id: 'default', label: 'Preto', value: '#111827' },
    { id: 'slate', label: 'Cinza', value: '#475569' },
    { id: 'sky', label: 'Azul', value: '#0284c7' },
    { id: 'emerald', label: 'Verde', value: '#059669' },
    { id: 'amber', label: 'Âmbar', value: '#d97706' },
    { id: 'rose', label: 'Rosa', value: '#e11d48' },
    { id: 'violet', label: 'Violeta', value: '#7c3aed' },
    { id: 'white', label: 'Branco', value: '#f8fafc' },
  ] as const;

  const applyTextColor = (color: string) => {
    runCommand(() => exec('foreColor', color));
    setColorOpen(false);
  };

  const insertBlockquote = () => {
    runCommand(() =>
      exec(
        'insertHTML',
        '<blockquote class="border-l-4 border-blue-500 pl-4 my-4 text-gray-700 italic">Citação…</blockquote><p><br></p>'
      )
    );
  };

  const insertCodeSample = () => {
    runCommand(() =>
      exec(
        'insertHTML',
        '<pre class="my-4 overflow-x-auto rounded-xl border border-gray-700 bg-[#0d1117] p-4 text-left text-sm leading-relaxed text-emerald-100 font-mono" data-help-inline-code="1"><code>// HTML, CSS, PHP…\n</code></pre><p><br></p>'
      )
    );
  };

  return (
    <div
      className={cn(
        floatingToolbar
          ? 'relative overflow-visible rounded-none border-0 bg-transparent text-left shadow-none'
          : sty.root,
        className
      )}
    >
      <div className={tb}>
        <ToolbarGroup>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('bold'))}
            aria-label="Negrito"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('italic'))}
            aria-label="Itálico"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('underline'))}
            aria-label="Sublinhado"
          >
            <Underline className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('strikeThrough'))}
            aria-label="Riscado"
          >
            <Strikethrough className="h-4 w-4" />
          </Button>
        </ToolbarGroup>
        <ToolbarSep tone={tone} />
        <ToolbarGroup>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('insertUnorderedList'))}
            aria-label="Lista"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('insertOrderedList'))}
            aria-label="Lista numerada"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </ToolbarGroup>
        <ToolbarSep tone={tone} />
        <ToolbarGroup>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              saveSelection();
              setSmilieDlg(true);
            }}
            aria-label="Inserir smilie"
          >
            <Smile className="h-4 w-4" />
          </Button>
        </ToolbarGroup>
        {toolbar === 'extended' ? (
          <>
            <ToolbarSep tone={tone} />
            <ToolbarGroup>
              <Popover open={colorOpen} onOpenChange={setColorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className={tbBtn}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      saveSelection();
                    }}
                    aria-label="Cor do texto"
                    title="Cor do texto"
                  >
                    <Palette className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-auto border-white/10 bg-[#0a0830] p-2 shadow-xl"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <p className="mb-2 px-1 font-inter text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Cor do texto
                  </p>
                  <div className="grid grid-cols-4 gap-1.5">
                    {TEXT_COLORS.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        title={c.label}
                        aria-label={c.label}
                        className="h-8 w-8 rounded-lg border border-white/15 shadow-inner transition hover:scale-105 hover:ring-2 hover:ring-sky-400/40"
                        style={{ backgroundColor: c.value }}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyTextColor(c.value)}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={insertLink}
                aria-label="Inserir link"
              >
                <Link2 className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  saveSelection();
                  setImageDlg(true);
                }}
                aria-label="Inserir imagem"
              >
                <ImagePlus className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={insertBlockquote}
                aria-label="Citação"
              >
                <Quote className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={insertCodeSample}
                aria-label="Trecho em consola (HTML/CSS/PHP…)"
              >
                <Code2 className="h-4 w-4" />
              </Button>
            </ToolbarGroup>
            <ToolbarSep tone={tone} />
            <ToolbarGroup>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  saveSelection();
                  setPowerBbcodeMode('mention');
                }}
                aria-label="Mencionar power [[power]]"
                title="Mencionar power"
              >
                <PowerNavIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  saveSelection();
                  setPowerBbcodeMode('smilies');
                }}
                aria-label="Galeria smilies [[smilies:power]]"
                title="Galeria de smilies"
              >
                <SmileNavIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  saveSelection();
                  setPowerBbcodeMode('pawns');
                }}
                aria-label="Galeria peões [[pawns:power]]"
                title="Galeria de peões"
              >
                <PawnNavIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className={tbBtn}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  saveSelection();
                  setAceDlg(true);
                }}
                aria-label="Mencionar Ace [[ace:slug]]"
                title="Mencionar Ace"
              >
                <AceNavIcon className="h-4 w-4" />
              </Button>
            </ToolbarGroup>
          </>
        ) : null}
        <ToolbarSep tone={tone} />
        <ToolbarGroup>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('undo'))}
            aria-label="Desfazer"
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={tbBtn}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => runCommand(() => exec('redo'))}
            aria-label="Refazer"
          >
            <Redo2 className="h-4 w-4" />
          </Button>
          {toolbar === 'extended' ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className={tbBtn}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => runCommand(() => exec('removeFormat'))}
              aria-label="Limpar formatação"
            >
              <RemoveFormatting className="h-4 w-4" />
            </Button>
          ) : null}
        </ToolbarGroup>
      </div>
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        className={cn(
          editorMinHeightClass,
          floatingToolbar
            ? cn(
                'rounded-lg px-1.5 py-1 text-left font-inter text-base leading-relaxed text-blue-100/75',
                'transition-[background-color,box-shadow] duration-200',
                'hover:bg-white/[0.06] focus:bg-white/[0.06] focus:ring-1 focus:ring-sky-400/30',
                '[&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
                '[&_a]:text-sky-300 [&_a]:underline [&_blockquote]:border-l [&_blockquote]:border-sky-500/50 [&_blockquote]:pl-3',
                '[&_.cms-rt-resize]:max-w-full [&_.cms-rt-resize_img]:max-w-full [&_img[data-xat-smilie]]:inline-block',
                'outline-none ring-0'
              )
            : sty.editor,
          disabled && 'opacity-50'
        )}
        onInput={onInput}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onFocus={() => {
          if (blurHideTimer.current) clearTimeout(blurHideTimer.current);
          if (floatingToolbar) setToolbarVisible(true);
        }}
        onBlur={() => {
          saveSelection();
          sync();
          if (!floatingToolbar) return;
          blurHideTimer.current = setTimeout(() => {
            if (dialogOpenRef.current) return;
            setToolbarVisible(false);
          }, 180);
        }}
      />
      <InsertImageDialog
        open={imageDlg}
        onOpenChange={setImageDlg}
        onInsert={insertHtml}
        imageVariant={imageVariant}
      />
      <InsertLinkDialog
        open={linkDlg}
        onOpenChange={setLinkDlg}
        selectedText={linkSelectedText}
        onInsert={applyLink}
      />
      <InsertSmilieDialog
        open={smilieDlg}
        onOpenChange={setSmilieDlg}
        onInsert={insertHtml}
        dark
      />
      <InsertPowerBbcodeDialog
        open={powerBbcodeMode != null}
        onOpenChange={(v) => {
          if (!v) setPowerBbcodeMode(null);
        }}
        mode={powerBbcodeMode ?? 'mention'}
        onInsert={insertHtml}
        dark
      />
      <InsertAceMentionDialog
        open={aceDlg}
        onOpenChange={setAceDlg}
        onInsert={insertHtml}
        dark
      />
    </div>
  );
}
